import { useState } from "react";
import { ArrowRight, CalendarDays, ChartNoAxesCombined, RefreshCw } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { ErrorState, LoadingState } from "../../components/ui/Feedback";

const PRESETS = [["today", "今天"], ["yesterday", "昨天"], ["this_week", "本周"], ["last_week", "上周"], ["last_7_days", "近 7 天"]];
const number = (value) => Number(value || 0).toLocaleString("zh-CN");
const OPERATION_LABELS = { translation: "翻译与分类", research: "客户背调", reply: "回复生成", reply_validation: "回复校验", inquiry_understanding: "需求理解", whatsapp: "WhatsApp 接待" };

function CustomRange({ period, onApply }) {
  const [start, setStart] = useState(period?.start_date || "");
  const [end, setEnd] = useState(period?.end_date || "");
  const [error, setError] = useState("");
  const submit = (event) => {
    event.preventDefault();
    if (start > end) { setError("开始日期不能晚于结束日期。"); return; }
    if ((Date.parse(end) - Date.parse(start)) / 86_400_000 >= 93) { setError("单次最多查询 93 天。"); return; }
    setError("");
    onApply({ period: "custom", start_date: start, end_date: end });
  };
  return <form className="dashboard-date-form" onSubmit={submit}>
    <label>开始日期<input type="date" required value={start} max={period?.today} onChange={(event) => { setStart(event.target.value); setError(""); }} /></label>
    <label>结束日期<input type="date" required value={end} min={start || undefined} max={period?.today} onChange={(event) => { setEnd(event.target.value); setError(""); }} /></label>
    <Button type="submit" icon={CalendarDays}>查看日期</Button>
    {error && <span className="dashboard-error" role="alert">{error}</span>}
  </form>;
}

export function PeriodControls({ report }) {
  const period = report.data?.period;
  return <section className="surface dashboard-period-controls" aria-label="工作台日期筛选">
    <div className="dashboard-presets" role="group" aria-label="快捷日期">
      {PRESETS.map(([value, label]) => <button key={value} type="button" aria-pressed={report.selection.period === value} className={report.selection.period === value ? "is-active" : ""} onClick={() => report.select({ period: value })}>{label}</button>)}
      {report.selection.period === "custom" && <span className="dashboard-custom-label">自定义日期</span>}
    </div>
    <CustomRange key={`${period?.start_date}:${period?.end_date}`} period={period} onApply={report.select} />
  </section>;
}

function Change({ current, previous }) {
  if (!previous) return <small className="dashboard-comparison">上期 {number(previous)} 封</small>;
  const delta = ((current - previous) / previous) * 100;
  return <small className="dashboard-comparison">较上期 {delta > 0 ? "+" : ""}{delta.toFixed(1)}% · 上期 {number(previous)} 封</small>;
}

function Trend({ daily }) {
  const max = Math.max(1, ...daily.flatMap((row) => [row.received, row.sent]));
  const x = (index) => daily.length === 1 ? 380 : 48 + index * 664 / (daily.length - 1);
  const y = (value) => 160 - value / max * 120;
  const labelStep = Math.max(1, Math.ceil((daily.length - 1) / 6));
  return <div className="dashboard-trend">
    <div className="dashboard-chart-heading"><h2><ChartNoAxesCombined size={18} />每日收件与回复</h2><div className="dashboard-chart-legend"><span><i className="is-received" />收到询盘</span><span><i className="is-sent" />实际已回复</span></div></div>
    <svg viewBox="0 0 760 200" role="img" aria-label="所选时段每日收到询盘与实际回复数量；完整数值见下方每日明细表">
      {[0, 0.5, 1].map((factor) => <g key={factor}><line x1="48" x2="712" y1={y(max * factor)} y2={y(max * factor)} className="dashboard-gridline" /><text x="36" y={y(max * factor) + 4} textAnchor="end">{Number((max * factor).toFixed(1))}</text></g>)}
      {["received", "sent"].map((field) => <g key={field} className={`dashboard-series is-${field}`}>
        <polyline points={daily.map((row, index) => `${x(index)},${y(row[field])}`).join(" ")} />
        {daily.map((row, index) => <circle key={row.date} cx={x(index)} cy={y(row[field])} r="3"><title>{row.date} · {field === "received" ? "收到询盘" : "实际已回复"} {row[field]} 封</title></circle>)}
      </g>)}
      {daily.map((row, index) => (index % labelStep === 0 || index === daily.length - 1) && <text key={row.date} x={x(index)} y="186" textAnchor="middle">{row.date.slice(5)}</text>)}
    </svg>
  </div>;
}

export function PeriodSummary({ report }) {
  if (!report.data) return report.error
    ? <ErrorState message={report.error} onRetry={report.reload} />
    : <LoadingState label="正在汇总所选日期…" />;
  const { summary, previous_summary: previous, period, generated_at: generatedAt } = report.data;
  const cards = [
    ["收到询盘", summary.received, "按收件时间统计", "received"],
    ["实际已回复", summary.sent, "按发送时间统计，含早期询盘", "sent"],
    ["高价值询盘", summary.high_value, `本期收件中，当前 ≥ ${report.data.high_value_min_rating} 星`],
    ["已有回复草稿", summary.draft_ready, "本期收件中，当前已有草稿"],
    ["当前待审核", summary.pending_review, "本期收件中，当前待审核"],
    ["当前处理失败", summary.failed, "本期收件中，当前处理失败"],
  ];
  return <>
    <div className="dashboard-period-description">
      <div><strong>{period.start_date} 至 {period.end_date}</strong><span>共 {period.days} 天 · {period.timezone} · 上周按周一至周日</span></div>
      <span role="status">{report.loading ? "正在更新…" : `更新于 ${new Intl.DateTimeFormat("zh-CN", { timeZone: period.timezone, hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(new Date(generatedAt))}`}</span>
    </div>
    {report.error && <div className="dashboard-stale" role="alert">更新失败，当前保留上次数据。{report.error}<Button size="sm" onClick={report.reload}>重试</Button></div>}
    <section className="dashboard-report-metrics" aria-label="所选日期业务指标">
      {cards.map(([label, value, caption, field]) => <article className="surface dashboard-report-metric" key={label}><small>{label}</small><strong>{number(value)}</strong><p>{caption}</p>{field && <Change current={value} previous={previous[field]} />}</article>)}
    </section>
    <p className="dashboard-measurement-note">对比区间：{period.previous_start_date} 至 {period.previous_end_date}（上一等长日期区间{period.end_date === period.today ? "，今天尚未结束" : ""}）。处理状态随后续操作更新，不代表历史日终快照。</p>
  </>;
}

export function PeriodDetails({ report }) {
  if (!report.data) return null;
  const { daily, summary, operations } = report.data;
  return <section className="surface dashboard-daily-panel">
    <Trend daily={daily} />
    <div className="section-header"><div><h2>每日明细</h2><p>收件按当天归档；发送按当天实际发送记录统计，因此可能多于当天收件。</p></div><Button size="sm" icon={RefreshCw} onClick={report.reload} loading={report.loading}>刷新统计</Button></div>
    <div className="dashboard-table-scroll" tabIndex={0} role="region" aria-label="每日统计明细，可横向滚动">
      <table className="dashboard-daily-table">
        <caption className="sr-only">每日收件、当前处理状态、当日发送及 AI 使用量</caption>
        <thead><tr><th scope="col">日期</th><th scope="col">收到询盘</th><th scope="col">已有背调</th><th scope="col">已有草稿</th><th scope="col">待审核</th><th scope="col">失败</th><th scope="col">无需回复</th><th scope="col">实际已回复</th><th scope="col">AI 调用</th><th scope="col">Token</th></tr></thead>
        <tbody>{[...daily].reverse().map((row) => <tr key={row.date}>
          <th scope="row"><button type="button" title={`查看 ${row.date} 当日统计`} onClick={() => report.select({ period: "custom", start_date: row.date, end_date: row.date })}>{row.date} <ArrowRight size={12} /></button></th>
          {["received", "researched", "draft_ready", "pending_review", "failed", "ignored", "sent", "ai_calls", "tokens"].map((field) => <td key={field}>{number(row[field])}</td>)}
        </tr>)}</tbody>
        <tfoot><tr><th scope="row">合计</th>{["received", "researched", "draft_ready", "pending_review", "failed", "ignored", "sent", "ai_calls", "tokens"].map((field) => <td key={field}>{number(summary[field])}</td>)}</tr></tfoot>
      </table>
    </div>
    <div className="dashboard-ai-summary">
      <div><h3>本期 AI 使用</h3><p>按当前账号调用日志统计，包含 WhatsApp 等业务；一次询盘可能调用多次 AI。成功率表示接口调用成功率。</p></div>
      <dl><div><dt>调用失败</dt><dd>{number(summary.ai_failed)} 次</dd></div><div><dt>调用成功率</dt><dd>{summary.ai_success_rate == null ? "—" : `${summary.ai_success_rate}%`}</dd></div><div><dt>成功调用平均耗时</dt><dd>{summary.ai_average_seconds == null ? "—" : `${summary.ai_average_seconds} 秒`}</dd></div></dl>
      <div className="dashboard-operation-list">{operations.length ? operations.map((item) => <span key={item.operation}>{OPERATION_LABELS[item.operation] || item.operation} <strong>{number(item.calls)} 次</strong> · {number(item.tokens)} Token · 失败 {number(item.failed)} 次</span>) : <span>所选时段没有 AI 调用记录</span>}</div>
    </div>
  </section>;
}
